import { ChevronDownIcon, SquareArrowOutUpRightIcon } from 'lucide-react'

import { employersEmployees, employersEmployerInvoices } from 'rails_routes'
import { SolidCircleCheckIcon } from 'slab'
import { cn, getFormattedDate, months } from 'utils'
import { type EmployersEnrollmentWindowClosingImportantDateItem, type EmployersEnrollmentWindowClosingImportantDateItemEnrollmentStatus, type EmployersImportantDate } from 'utils/web_api.schemas'

const HUMAN_READABLE_ENROLLMENT_STATUS: Record<EmployersEnrollmentWindowClosingImportantDateItemEnrollmentStatus, string> = {
  cobra: 'COBRA',
  terminated: 'Terminated',
  termination_scheduled: 'Termination scheduled',
  payroll_deactivated: 'Payroll deactivated',
  enrolled: 'Enrolled',
  declined: 'Declined',
  expired: 'Expired',
  invited: 'Invited',
  invite_pending: 'Invite pending',
  not_invited: 'Not invited',
  in_progress: 'Viewing plans',
  pending_automated_invite: 'Pending automated invite',
  unknown: 'Unknown',
}

const EnrollmentWindowClosingDetails = ({ items }: { items: EmployersEnrollmentWindowClosingImportantDateItem[] }) => (
  <div>
    <div className='body-sm text-main space-y-1'>
      {items.map((item) => (
        <a
          key={item.employee_id}
          href={employersEmployees.show({ id: item.employee_id })}
          className='flex items-end justify-between font-normal text-gray-600 hover:text-gray-600 group'
          target='_blank'
          rel='noreferrer'
        >
          <span className='group-hover:underline group-hover:font-medium whitespace-nowrap flex items-center gap-1'>
            {item.employee_name}
            <SquareArrowOutUpRightIcon className='w-0 h-0 group-hover:w-3 group-hover:h-3 transition-all duration-200' />
          </span>
          <span className='flex-1 h-px mx-2 bg-[length:8px_1px] bg-repeat-x bg-[radial-gradient(circle,_gray_1px,_transparent_1px)]' />
          <span className='whitespace-nowrap flex items-center gap-1'>
            {item.enrollment_status === 'enrolled' && (
              <SolidCircleCheckIcon className='w-3 h-3' color='green' />
            )}
            {HUMAN_READABLE_ENROLLMENT_STATUS[item.enrollment_status]}
          </span>
        </a>
      ))}
    </div>
  </div>
)

const UpcomingBillingDateDetails = ({ month }: { month: string }) => (
  <div>
    Your healthcare costs for {month} insurance coverage will be debited.
    <a
      href={employersEmployerInvoices.index()}
      className='text-gray-600 hover:text-gray-700 underline decoration-dotted mt-1'
      target='_blank' rel='noreferrer'
    >
      <div className='flex items-center gap-1'>
        <span>View all invoices</span>
        <SquareArrowOutUpRightIcon className='w-3 h-3' />
      </div>
    </a>
  </div>
)

const EnrollmentCutoffDetails = ({ date }: { date: Date }) => (
  <span>
    Employees must enroll by {getFormattedDate(date.toISOString(), { month: 'short', day: 'numeric', year: undefined })} in order to receive coverage by {months[date.getMonth() + 1]} 1st.
  </span>
)

const PriorYearQmeReimbursementCutoffDetails = ({ date }: { date: Date }) => (
  <span>
    The final day for employees to file for reimbursements for Qualified Medical Expenses for the {date.getFullYear()} plan year is {getFormattedDate(date.toISOString(), { month: 'long', day: 'numeric', year: undefined })}.
  </span>
)

export default function ImportantDate (
  { importantDate, expanded, onHeaderClick }:
  { importantDate: EmployersImportantDate, expanded: boolean, onHeaderClick: () => void }) {
  return (
    <div className={cn('hover:bg-gray-100 rounded-lg border', {
      'bg-gray-100 border-color-stroke-light': expanded,
      'border-transparent': !expanded,
    })}
    >
      <div
        className={cn('body-sm flex items-center justify-between cursor-pointer px-4 py-2 gap-x-2 border-b', {
          'border-color-stroke-light': expanded,
          'border-transparent': !expanded,
        })}
        onClick={onHeaderClick}
      >
        <div className='flex items-center gap-2'>
          <span className='w-16 shrink-0 text-gray-500 whitespace-nowrap'>{getFormattedDate(importantDate.date, { month: 'short', day: 'numeric', year: undefined })}</span>
          <span className='text-main'>{importantDate.title}</span>
        </div>
        <ChevronDownIcon
          size={16}
          className={cn('transition-transform duration-200 ', { 'rotate-180': expanded })}
        />
      </div>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className='overflow-hidden'>
          <div className='px-4 py-2 ml-[calc(4rem+0.5rem)]'>
            <div className='body-sm text-main'>
              {importantDate.type === 'enrollment_window_closing' && importantDate.items && (
                <EnrollmentWindowClosingDetails items={importantDate.items} />
              )}
              {importantDate.type === 'upcoming_billing_date' && (
                <UpcomingBillingDateDetails month={getFormattedDate(importantDate.date, { month: 'long', day: undefined, year: undefined })} />
              )}
              {importantDate.type === 'enrollment_cutoff' && (
                <EnrollmentCutoffDetails date={new Date(importantDate.date)} />
              )}
              {importantDate.type === 'prior_year_qme_reimbursement_cutoff' && (
                <PriorYearQmeReimbursementCutoffDetails date={new Date(importantDate.date)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
